'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { MiniChatWidget } from './MiniChatWidget';
import { ChatFloatingButton } from './ChatFloatingButton';
import { useChatStore } from '../model/useChatStore';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';

interface ChatWidgetContainerProps {
  className?: string;
}

/**
 * Chat Widget Container - Manages chat widget visibility and floating button
 *
 * Shows:
 * - Floating button when widget is closed
 * - Mini chat widget when opened
 *
 * Automatically:
 * - Connects to WebSocket when authenticated
 * - Shows unread count on button
 */
export const ChatWidgetContainer = ({ className }: ChatWidgetContainerProps) => {
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const router = useRouter();
  const locale = useLocale();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const {
    conversations,
    loadConversations,
    connectSocket,
    disconnectSocket,
  } = useChatStore();

  // Calculate total unread count
  const totalUnreadCount = conversations.reduce(
    (sum, conv) => sum + (conv.unreadCount || 0),
    0
  );

  // Initialize chat connection when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    connectSocket();
    loadConversations();

    return () => {
      disconnectSocket();
    };
  }, [isAuthenticated, connectSocket, disconnectSocket, loadConversations]);

  // Handle open widget
  const handleOpenWidget = () => {
    setIsWidgetOpen(true);
  };

  // Handle close widget
  const handleCloseWidget = () => {
    setIsWidgetOpen(false);
  };

  // Handle extend to full chat page
  const handleExtendWidget = (conversationId: string | null) => {
    // Close the mini widget
    setIsWidgetOpen(false);

    // Navigate to profile page with messages tab (where full chat is located)
    const profilePath = `/${locale}/profile?tab=messages`;

    // Navigate to profile page with optional conversation ID
    if (conversationId) {
      router.push(`${profilePath}&conversationId=${conversationId}`);
    } else {
      router.push(profilePath);
    }
  };

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Floating button - shown when widget is closed */}
      {!isWidgetOpen && (
        <ChatFloatingButton
          onClick={handleOpenWidget}
          unreadCount={totalUnreadCount}
          className={className}
        />
      )}

      {/* Mini chat widget - shown when opened */}
      <MiniChatWidget
        isOpen={isWidgetOpen}
        onClose={handleCloseWidget}
        onExtend={handleExtendWidget}
        initialState="chatsList"
      />
    </>
  );
};
